/**
 * ReadonlyKanbanColumn — coluna compartilhada dos Kanbans somente leitura.
 *
 * Mantém cabeçalho, skeleton, rolagem independente e carregamento automático
 * quando a primeira página ainda não cria overflow.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Extração da base compartilhada.
 */
import { useCallback, useEffect, useRef, type ReactNode, type UIEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface ReadonlyKanbanColumnProps {
  label: string;
  count: number;
  barClass: string;
  headerClass: string;
  children: ReactNode;
  itemCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
  emptyLabel: string;
  headerDetail?: ReactNode;
}

/** Coluna fixa de 300 px com paginação infinita e estados visuais uniformes. */
export function ReadonlyKanbanColumn({
  label,
  count,
  barClass,
  headerClass,
  children,
  itemCount,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  emptyLabel,
  headerDetail,
}: ReadonlyKanbanColumnProps) {
  const loadingMoreRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    void fetchNextPage().finally(() => {
      loadingMoreRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const viewport = event.currentTarget;
    if (viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 200) loadMore();
  }, [loadMore]);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isLoading || loadingMoreRef.current) return;
    const viewport = contentRef.current?.closest<HTMLElement>('[data-radix-scroll-area-viewport]');
    if (viewport && viewport.scrollHeight <= viewport.clientHeight + 8) loadMore();
  }, [hasNextPage, isFetchingNextPage, isLoading, itemCount, loadMore]);

  return (
    <div className="flex h-full w-[300px] shrink-0 flex-col overflow-hidden rounded-lg bg-muted/30">
      <div className={cn('shrink-0 border-b border-border/50 rounded-t-lg px-3 py-2', headerClass)}>
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', barClass)} />
          <span className="truncate text-xs font-semibold">{label}</span>
          <span className="ml-auto shrink-0 rounded bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground tabular-nums">
            {count}
          </span>
        </div>
        {headerDetail}
      </div>

      <ScrollArea
        className="flex-1 h-0 min-h-0 p-2"
        axis="y"
        preventScrollChaining
        touchAxis="pan-y"
        onScrollCapture={handleScroll}
      >
        <div ref={contentRef} className="w-full min-w-0 space-y-2">
          {isLoading ? (
            <>
              <Skeleton className="h-24 w-full rounded-lg" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </>
          ) : itemCount === 0 ? (
            <p className="py-8 text-center text-[11px] text-muted-foreground">{emptyLabel}</p>
          ) : children}

          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}